import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { SITE, SERVICE_OPTIONS } from "@/lib/site";
import { Send, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  full_name: "",
  company: "",
  email: "",
  phone: "",
  origin: "",
  destination: "",
  service_type: SERVICE_OPTIONS[0],
  load_description: "",
  weight: "",
  dimensions: "",
  pickup_date: "",
  cross_border: false,
};

const inputCls =
  "w-full bg-black border border-[#222] focus:border-[#1E5BFF] focus:outline-none focus:ring-1 focus:ring-[#1E5BFF] text-white placeholder:text-white/30 px-4 py-3 text-sm transition-colors";
const labelCls =
  "font-display text-[11px] font-bold uppercase tracking-[0.2em] text-white/70 mb-2 block";

export const QuoteForm = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setSuccess(true);
      setForm(initial);
      toast.success("Quote request received — dispatch will reach out shortly.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Could not submit. Please call dispatch.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="relative bg-[#0d0d0d] py-24 lg:py-32 border-t border-[#161616] overflow-hidden"
    >
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-[#1E5BFF]/10 blur-[140px] rounded-full pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="inline-flex items-center gap-3 mb-5">
            <span className="h-[2px] w-10 bg-[#1E5BFF]" />
            <span className="font-display text-xs font-bold uppercase tracking-[0.3em] text-[#1E5BFF]">
              Request a Quote
            </span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase text-white tracking-tighter leading-[0.95]">
            Got a Load?
            <br />
            <span className="text-[#1E5BFF]">Let's Move It.</span>
          </h2>
          <p className="mt-6 text-slate-400 text-base md:text-lg leading-relaxed max-w-md">
            Tell us about your freight. The more detail you give us, the faster dispatch can confirm
            rates, equipment, and pickup. Need it moved today?{" "}
            <a
              href={`tel:${SITE.contact.phone.replace(/\D/g, "")}`}
              className="text-white border-b border-[#1E5BFF] hover:text-[#1E5BFF] transition-colors"
            >
              Call 24/7 Dispatch
            </a>
            .
          </p>

          <div className="mt-10 space-y-4">
            <div className="text-sm text-slate-400">
              <span className={labelCls}>Email</span>
              <a href={`mailto:${SITE.contact.email}`} className="text-white hover:text-[#1E5BFF] transition-colors">
                {SITE.contact.email}
              </a>
            </div>
            <div className="text-sm text-slate-400">
              <span className={labelCls}>Dispatch</span>
              <span className="text-white">{SITE.contact.hours}</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {success ? (
            <div
              data-testid="quote-success"
              className="card-industrial p-10 lg:p-14 min-h-[420px] flex flex-col items-start justify-center"
            >
              <CheckCircle2 size={48} className="text-[#1E5BFF] mb-6" />
              <h3 className="font-display text-3xl font-black uppercase text-white tracking-tight">
                Quote Received.
              </h3>
              <p className="mt-4 text-slate-400 max-w-md leading-relaxed">
                We've got your request. Dispatch will get back to you with rates and equipment
                availability shortly. For urgent loads, give us a call.
              </p>
              <button
                type="button"
                onClick={() => setSuccess(false)}
                data-testid="quote-submit-another"
                className="btn-slash mt-8 bg-[#1E5BFF] hover:bg-[#3B72FF] text-white font-display font-bold uppercase tracking-[0.18em] text-xs px-8 py-3"
              >
                Submit Another
              </button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="quote-form"
              className="card-industrial p-6 sm:p-10 space-y-5"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls} htmlFor="qf-name">Full Name *</label>
                  <input
                    id="qf-name"
                    data-testid="quote-input-name"
                    required
                    value={form.full_name}
                    onChange={update("full_name")}
                    className={inputCls}
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="qf-company">Company</label>
                  <input
                    id="qf-company"
                    data-testid="quote-input-company"
                    value={form.company}
                    onChange={update("company")}
                    className={inputCls}
                    placeholder="Company Inc."
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="qf-email">Email *</label>
                  <input
                    id="qf-email"
                    type="email"
                    data-testid="quote-input-email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    className={inputCls}
                    placeholder="you@company.com"
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="qf-phone">Phone *</label>
                  <input
                    id="qf-phone"
                    data-testid="quote-input-phone"
                    required
                    value={form.phone}
                    onChange={update("phone")}
                    className={inputCls}
                    placeholder="(403) 555-0100"
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="qf-origin">Origin *</label>
                  <input
                    id="qf-origin"
                    data-testid="quote-input-origin"
                    required
                    value={form.origin}
                    onChange={update("origin")}
                    className={inputCls}
                    placeholder="Calgary, AB"
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="qf-destination">Destination *</label>
                  <input
                    id="qf-destination"
                    data-testid="quote-input-destination"
                    required
                    value={form.destination}
                    onChange={update("destination")}
                    className={inputCls}
                    placeholder="Fort McMurray, AB"
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="qf-service">Service Type *</label>
                  <select
                    id="qf-service"
                    data-testid="quote-input-service"
                    value={form.service_type}
                    onChange={update("service_type")}
                    className={inputCls}
                  >
                    {SERVICE_OPTIONS.map((s) => (
                      <option key={s} value={s} className="bg-black">
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="qf-pickup">Pickup Date</label>
                  <input
                    id="qf-pickup"
                    type="date"
                    data-testid="quote-input-pickup"
                    value={form.pickup_date}
                    onChange={update("pickup_date")}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="qf-weight">Weight</label>
                  <input
                    id="qf-weight"
                    data-testid="quote-input-weight"
                    value={form.weight}
                    onChange={update("weight")}
                    className={inputCls}
                    placeholder="e.g., 18,000 lbs"
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="qf-dims">Dimensions (L × W × H)</label>
                  <input
                    id="qf-dims"
                    data-testid="quote-input-dimensions"
                    value={form.dimensions}
                    onChange={update("dimensions")}
                    className={inputCls}
                    placeholder="e.g., 20' × 8' × 8'"
                  />
                </div>
              </div>

              <div>
                <label className={labelCls} htmlFor="qf-desc">Load Description *</label>
                <textarea
                  id="qf-desc"
                  data-testid="quote-input-description"
                  required
                  rows={4}
                  value={form.load_description}
                  onChange={update("load_description")}
                  className={inputCls + " resize-y min-h-[110px]"}
                  placeholder="Describe the load — equipment, materials, special handling, permits required, etc."
                />
              </div>

              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid="quote-input-crossborder"
                  checked={form.cross_border}
                  onChange={update("cross_border")}
                  className="w-4 h-4 accent-[#1E5BFF] bg-black border border-[#222]"
                />
                <span className="font-display text-xs font-bold uppercase tracking-[0.2em] text-white/80">
                  Cross-Border Shipment (Canada ↔ US)
                </span>
              </label>

              <button
                type="submit"
                disabled={submitting}
                data-testid="quote-submit-btn"
                className="btn-slash group inline-flex items-center gap-3 bg-[#1E5BFF] hover:bg-[#3B72FF] disabled:opacity-60 disabled:cursor-not-allowed text-white font-display font-bold uppercase tracking-[0.18em] text-sm px-10 py-4"
              >
                {submitting ? "Submitting…" : "Submit Quote Request"}
                <Send size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
